import { useState } from 'react';
import { Notification } from '@letrustech/letrus-ui';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  useFetchNotifications,
  useUpdateNotification,
} from 'APIs/letrusAPI/notifications';
import { Notification as NotificationType } from 'APIs/letrusAPI/notifications/types';
import useNotification from 'features/useNotification';
import { useMobile } from 'utils/hooks';

function DropdownNotifications() {
  const notifications = useFetchNotifications();
  const updateNotification = useUpdateNotification();

  const notificationsData = notifications?.data?.results || [];
  const unseenNotificationCount = notificationsData.filter(
    (notification) =>
      !notification.received &&
      notification.channel !== ('recommendation' as string)
  ).length;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [
    shouldShowOnlyUnseenNotifications,
    setShouldShowOnlyUnseenNotifications,
  ] = useState(false);

  const navigate = useNavigate();
  const { getLinkType, hasBeenSeen, getNotificationSeenState } =
    useNotification();
  const { isMobile } = useMobile();

  function markNotification(
    notificationId: number,
    notificationData: { received?: string; opened?: string }
  ) {
    updateNotification.trigger(
      {
        notificationId,
        notificationData,
      },
      {
        onSuccess: () => notifications.mutate(),
      }
    );
  }

  // Required to filter out "recommendation" notifications
  // since business rules specify they should not appear in the dropdown.
  const notificationList = notificationsData
    .filter(
      (notification) => notification.channel !== ('recommendation' as string)
    )
    .map((notification) => {
      const hasReceivedToday = dayjs(notification.created).isSame(
        dayjs(),
        'day'
      );

      return {
        id: notification.id,
        title: notification.title,
        description: notification.content,
        time: hasReceivedToday
          ? dayjs(notification.created).format('HH:mm')
          : dayjs(notification.created).format('DD/MM'),
        opened: hasBeenSeen(notification),
        hasReceivedToday,
        handleClick: () => onClickNotification(notification),
        withHeaderIcon: !!notification.link,
      };
    });

  function onClickNotification(notification: NotificationType) {
    const notificationSeenState = getNotificationSeenState(notification);
    const notificationLinkType = getLinkType(notification);

    if (notificationSeenState === 'not-received') {
      markNotification(notification.id, { received: new Date().toISOString() });
    }

    if (notificationSeenState === 'not-opened') {
      markNotification(notification.id, { opened: new Date().toISOString() });
    }

    if (!notification.link) {
      return;
    }

    if (notificationLinkType === 'internal') {
      navigate(notification.link);
    }

    if (notificationLinkType === 'external') {
      window.open(notification.link, '_blank');
    }
  }

  function onClickNotificationHistory() {
    if (notificationsData.length >= 1) {
      setIsDropdownOpen(false);
      navigate('/notificacoes');
    }
  }

  function onClickNotificationButton() {
    // TODO: Remove this call to onClickNotificationHistory after @letrustech/letrus-ui upgrade
    onClickNotificationHistory();

    if (isMobile) {
      onClickNotificationHistory();
    } else {
      setIsDropdownOpen(!isDropdownOpen);
    }

    const notReceivedYetNotifications = notificationsData.filter(
      (notification) =>
        !notification.received &&
        notification.channel !== ('recommendation' as string)
    );

    if (notReceivedYetNotifications.length) {
      notReceivedYetNotifications.forEach((notification) => {
        markNotification(notification.id, {
          received: new Date().toISOString(),
        });
      });
    }
  }

  return (
    <div className="Notifications" data-testid="notification-button">
      <Notification.Dropdown
        notificationState={unseenNotificationCount ? 'new' : 'none'}
        isDropdownOpen={isDropdownOpen}
        onClickNotificationButton={() => onClickNotificationButton()}
        onClickOutsideDropdown={() => setIsDropdownOpen(false)}
        notificationCount={unseenNotificationCount}
        onClickFilterUnseenNotifications={() => {
          setShouldShowOnlyUnseenNotifications(
            !shouldShowOnlyUnseenNotifications
          );
        }}
      >
        {!!notificationsData.length && (
          <Notification.List
            items={
              shouldShowOnlyUnseenNotifications
                ? notificationList.filter((item) => !item.opened)
                : notificationList
            }
          />
        )}
      </Notification.Dropdown>
    </div>
  );
}

export default DropdownNotifications;
